import React from "react";
import {
  GDPRProcessorRecommendation,
  OrganizationDataRecipientDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { VendorFlagWithDocumentation, VendorRadioOption } from "./VendorFlagWithDocumentation";
import { DataRecipientPatchMutation } from "../../../hooks/useDataRecipients";
import { PatchOp } from "../../../../common/util/jsonPatch";

type GDPRProcessorLanguageLocatorProps = {
  recipients: OrganizationDataRecipientDto[];
  patchVendor: DataRecipientPatchMutation;
};

type GDPRProcessorRecommendationKey = "processor" | "controller" | "unknown";

export const PROCESSOR_CLASSIFICATION_OPTIONS: VendorRadioOption<
  GDPRProcessorRecommendation,
  GDPRProcessorRecommendationKey
>[] = [
  {
    value: "Processor",
    type: "file",
    label: "I have confirmed this data recipient is a processor",
    key: "processor",
  },
  {
    value: "Controller",
    type: "file",
    label: "I have confirmed this data recipient is a controller",
    key: "controller",
  },
  {
    value: "Unknown",
    type: "normal",
    label: "I'm not sure how to classify this data recipient",
    key: "unknown",
  },
];

export const GDPRProcessorLanguageLocator: React.FC<GDPRProcessorLanguageLocatorProps> = ({
  recipients,
  patchVendor,
}) => {
  const updateFlag = async (
    recipient: OrganizationDataRecipientDto,
    setting: GDPRProcessorRecommendation,
  ) => {
    await patchVendor.mutate({
      vendorId: recipient.vendorId,
      operations: [
        PatchOp.replace("/gdprProcessorSetting", setting),
        PatchOp.replace("/gdprContactProcessorStatus", null),
      ],
    });
  };

  const updateUrl = async (
    recipient: OrganizationDataRecipientDto,
    url: string,
    key: GDPRProcessorRecommendationKey,
  ) => {
    const operations = [];

    if (key === "processor") {
      operations.push(PatchOp.replace("/gdprProcessorSetting", "Processor"));
      operations.push(PatchOp.replace("/gdprProcessorGuaranteeUrl", url));
    } else if (key === "controller") {
      operations.push(PatchOp.replace("/gdprProcessorSetting", "Controller"));
      operations.push(PatchOp.replace("/gdprControllerGuaranteeUrl", url));
    }

    await patchVendor.mutate({
      vendorId: recipient.vendorId,
      operations,
    });
  };

  const updateFile = async (
    recipient: OrganizationDataRecipientDto,
    fileName: string,
    fileKey: string,
    key: GDPRProcessorRecommendationKey,
  ) => {
    const operations = [];

    if (key === "processor") {
      operations.push(PatchOp.replace("/gdprProcessorGuaranteeFileName", fileName));
      operations.push(PatchOp.replace("/gdprProcessorGuaranteeFileKey", fileKey));
    } else if (key === "controller") {
      operations.push(PatchOp.replace("/gdprControllerGuaranteeFileName", fileName));
      operations.push(PatchOp.replace("/gdprControllerGuaranteeFileKey", fileKey));
    }
    await patchVendor.mutate({
      vendorId: recipient.vendorId,
      operations,
    });
  };

  return (
    <VendorFlagWithDocumentation
      options={PROCESSOR_CLASSIFICATION_OPTIONS}
      recipients={recipients}
      onFlagChanged={updateFlag}
      onUrlChanged={updateUrl}
      onFileChanged={updateFile}
      getFlag={(r) => r.gdprProcessorSetting}
      getUrl={(r) =>
        r.gdprProcessorSetting === "Processor"
          ? r.gdprProcessorGuaranteeUrl
          : r.gdprControllerGuaranteeUrl
      }
      getFileName={(r) =>
        r.gdprProcessorSetting === "Processor"
          ? r.gdprProcessorGuaranteeFileName
          : r.gdprControllerGuaranteeFileName
      }
      getFileKey={(r) =>
        r.gdprProcessorSetting === "Processor"
          ? r.gdprProcessorGuaranteeFileKey
          : r.gdprControllerGuaranteeFileKey
      }
    />
  );
};
